import { getFunctions, httpsCallable, type HttpsCallableOptions } from 'firebase/functions';
import { app } from './firebase';
import type { AiAction, TaskStatus, Priority } from '@/types';

const functions = getFunctions(app, 'us-central1');

// Input/output types are intentionally re-declared here.
// This is the wire contract — keep it independent of functions/src/lib/validators.ts
// so the client doesn't bundle Zod/server-only modules.

export interface ScheduleInput {
  tasks: {
    id: string;
    title: string;
    durationMinutes: number;
    priority?: 'low' | 'med' | 'high';
  }[];
  events: { startMs: number; endMs: number; title: string }[];
  energyPattern: { highHours: number[]; lowHours: number[] };
  targetDate: string;
  /**
   * JS Date.prototype.getTimezoneOffset() value — minutes WEST of UTC
   * (UTC+2 → -120, UTC-7 → +420). Lets the server anchor targetDate's LOCAL
   * day in UTC epoch ms instead of assuming the user lives at UTC+0.
   */
  tzOffsetMinutes: number;
  /** Optional. Epoch ms; AI will not schedule any task before this. */
  earliestStartMs?: number;
}

export interface ScheduleOutput {
  schedule: { taskId: string; startMs: number; endMs: number; reason: string }[];
}

export interface MicroStepInput {
  taskTitle: string;
  taskDescription?: string;
  durationMinutes: number;
}

export interface MicroStepOutput {
  firstStep: string;
  rationale: string;
}

export interface CompressInput {
  routineSteps: { name: string; durationMinutes: number }[];
  targetTotalMinutes: number;
}

export interface CompressOutput {
  steps: { name: string; durationMinutes: number; kept: boolean; originalIndex: number }[];
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface TaskContext {
  id: string;
  title: string;
  status: TaskStatus;
  priority: Priority;
  durationMinutes: number;
  scheduledStartMs?: number;
}

export interface CommandInput {
  userMessage: string;
  context: {
    currentDate: string;
    timeOfDay: 'morning' | 'afternoon' | 'evening' | 'night';
    todayTaskCount?: number;
    nowMs: number;
    tzOffsetMinutes: number;
  };
  history?: ChatMessage[];
  tasks?: TaskContext[];
  forceNoStream?: boolean;
}

export interface CommandOutput {
  text: string;
  actions: AiAction[];
}

const httpsCallableOpts: HttpsCallableOptions = { limitedUseAppCheckTokens: false };

export const callAiSchedule = async (input: ScheduleInput): Promise<ScheduleOutput> =>
  (
    await httpsCallable<ScheduleInput, ScheduleOutput>(
      functions,
      'aiSchedule',
      httpsCallableOpts,
    )(input)
  ).data;

export const callAiMicroStep = async (input: MicroStepInput): Promise<MicroStepOutput> =>
  (
    await httpsCallable<MicroStepInput, MicroStepOutput>(
      functions,
      'aiMicroStep',
      httpsCallableOpts,
    )(input)
  ).data;

export const callAiCompress = async (input: CompressInput): Promise<CompressOutput> =>
  (
    await httpsCallable<CompressInput, CompressOutput>(
      functions,
      'aiCompress',
      httpsCallableOpts,
    )(input)
  ).data;

export class StreamAbortedError extends Error {
  constructor() {
    super('stream aborted');
    this.name = 'StreamAbortedError';
  }
}

export async function callAiCommandStream(
  input: CommandInput,
  onChunk: (text: string) => void,
  opts?: { signal?: AbortSignal },
): Promise<CommandOutput> {
  const callable = httpsCallable<CommandInput, CommandOutput, { text: string }>(
    functions,
    'aiCommand',
    httpsCallableOpts,
  );
  const result = await callable.stream(input);
  let streamed = false;
  for await (const chunk of result.stream) {
    if (opts?.signal?.aborted) throw new StreamAbortedError();
    streamed = true;
    onChunk(chunk.text);
  }
  const final = await result.data;
  if (!streamed) {
    if (opts?.signal?.aborted) throw new StreamAbortedError();
    onChunk(final.text);
  }
  return final;
}

export const callAiCommandNoStream = async (input: CommandInput): Promise<CommandOutput> =>
  (
    await httpsCallable<CommandInput, CommandOutput>(
      functions,
      'aiCommand',
      httpsCallableOpts,
    )({ ...input, forceNoStream: true })
  ).data;
